// Open Pipe Club - Auth context
// Provides the current user, token handling and auth actions.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

const CACHED_USER_KEY = "opc_cached_user";

function getInitialUser() {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHED_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => getInitialUser());
  const [loading, setLoading] = useState(() => !getInitialUser() && !!api.getToken());

  const loadMe = useCallback(async () => {
    const token = api.getToken();
    if (!token) {
      setUser(null);
      if (typeof localStorage !== "undefined") localStorage.removeItem(CACHED_USER_KEY);
      setLoading(false);
      return;
    }
    try {
      const data = await api.auth.me();
      setUser(data);
      if (typeof localStorage !== "undefined") localStorage.setItem(CACHED_USER_KEY, JSON.stringify(data));
    } catch (e) {
      api.setToken(null);
      setUser(null);
      if (typeof localStorage !== "undefined") localStorage.removeItem(CACHED_USER_KEY);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (username, password) => {
    const data = await api.auth.login(username, password);
    if (data.token) api.setToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    if (typeof localStorage !== "undefined") localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
    return data;
  }, []);

  const register = useCallback(async (usernameOrPayload, password, inviteCode, email) => {
    let payload;
    if (typeof usernameOrPayload === "object" && usernameOrPayload !== null) {
      payload = usernameOrPayload;
    } else {
      payload = {
        username: usernameOrPayload,
        password,
        invite_code: inviteCode,
        email: email || ""
      };
    }
    const data = await api.auth.register(payload);
    if (data.token) api.setToken(data.token);
    const userData = data.user || data;
    setUser(userData);
    if (typeof localStorage !== "undefined") localStorage.setItem(CACHED_USER_KEY, JSON.stringify(userData));
    return data;
  }, []);

  const logout = useCallback(() => {
    api.setToken(null);
    setUser(null);
    if (typeof localStorage !== "undefined") localStorage.removeItem(CACHED_USER_KEY);
  }, []);

  const refresh = useCallback(() => loadMe(), [loadMe]);

  const hasRole = useCallback((allowedRoles) => {
    if (!user) return false;
    if (user.role === "admin" || user.is_admin) return true;
    const userRoleStr = (user.role || "").toLowerCase().trim();
    return (allowedRoles || []).some((r) => {
      const lr = r.toLowerCase().trim();
      return lr === userRoleStr || (lr.includes("personal") && userRoleStr.includes("personal")) || (lr.includes("hr") && (userRoleStr.includes("hr") || userRoleStr.includes("personal")));
    });
  }, [user]);

  const userRoleStr = (user?.role || "").toLowerCase().trim();
  const isAdmin = !!user && (user.is_admin === 1 || user.is_admin === true || ["admin", "inhaber", "stv. inhaber", "stv inhaber", "stellv. inhaber"].includes(userRoleStr));
  const isHRTeam = isAdmin || ["hr team", "hr-team", "personal team", "personal-team", "personalteam", "personal", "personalabteilung"].includes(userRoleStr) || userRoleStr.includes("personal") || (userRoleStr !== "fahrer" && userRoleStr !== "probefahrer" && userRoleStr !== "driver" && /\bhr\b/.test(userRoleStr));
  const canCreateNews = isAdmin || isHRTeam || ["event team", "event-team", "modding team", "modding-team"].includes(userRoleStr) || userRoleStr.includes("event") || userRoleStr.includes("modding");
  const canCreateEvents = isAdmin || ["event team", "event-team"].includes(userRoleStr) || userRoleStr.includes("event");
  const canUploadImages = !!user && (isAdmin || userRoleStr !== "probefahrer");

  const value = {
    user,
    token: api.getToken(),
    loading,
    isAuthenticated: !!user,
    isAdmin,
    isHRTeam,
    canCreateNews,
    canCreateEvents,
    canUploadImages,
    isManagement: !!user && (isAdmin || isHRTeam || ["manager", "event team", "event-team", "modding team", "modding-team"].includes(userRoleStr) || userRoleStr.includes("event") || userRoleStr.includes("modding") || userRoleStr.includes("personal")),
    hasRole,
    login,
    register,
    logout,
    refresh,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
